import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiService from "../services/api";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Tabs,
  Tab,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  InputAdornment,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import InfoIcon from "@mui/icons-material/Info";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";

// ----------
// AddProduct Page
// Put this file at: frontend/src/pages/AddProduct.jsx (or your pages/ directory)
// Install required deps:
// npm install @mui/material @mui/icons-material react-hook-form axios react-quill
// ----------

export default function AddProduct() {
  const navigate = useNavigate();
  const location = useLocation();
  const quillRef = useRef(null);

  // Check if we're in edit mode - only true on edit-product route
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(null);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      product_name_en: "",
      description_en: "",
      category_id: "",
      product_type: "physical",
      sku: "",
      unit: "pcs",
      tags: [],
      unit_price: "",
      min_order_qty: "1",
      stock_qty: "",
      discount_type: "flat",
      discount_amount: "",
      tax_percent: "",
      tax_calculation: "include",
      shipping_cost: "",
      shipping_multiply_quantity: false,
      status: "active",
      is_featured: false,
      video_link: "",
      meta_title: "",
      meta_description: "",
      indexing_option: "index",
      max_snippet: "-1",
      max_video_preview: "-1",
      max_image_preview: "large",
      specifications: {},
    },
  });

  // Handle location state properly to prevent infinite loops
  useEffect(() => {
    const isEditRoute =
      location.pathname === "/dashboard/edit-product" && location.state?.isEdit;
    setIsEditMode(isEditRoute);
    const product = location.state?.product || null;
    setEditingProduct(product);

    // Set original product snapshot for dirty field comparison
    if (product) {
      setOriginalProduct(product);
      reset(product); // Reset form with product data
    }

    // Clear localStorage when not in edit mode
    if (!isEditRoute) {
      localStorage.removeItem("editProductId");
      localStorage.removeItem("editProductData");
      setOriginalProduct(null);
    }
  }, [location.pathname, location.state, reset]);

  const [categories, setCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [enabledAttributes, setEnabledAttributes] = useState([]); // ["Color","Size"]
  const [selectedAttributes, setSelectedAttributes] = useState({});
  // { Color: ["Red"], Size: ["M","L"] }
  const [generatedVariations, setGeneratedVariations] = useState([]);
  const [selectedBooleanValues, setSelectedBooleanValues] = useState({});
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  const [occasionInput, setOccasionInput] = useState("");
  const [selectedProductColors, setSelectedProductColors] = useState([]);
  const [newSpecName, setNewSpecName] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [bulkSpecsInput, setBulkSpecsInput] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [showSingleSpec, setShowSingleSpec] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const customizationPositionOptions = ["Front", "Back", "Left Sleeve", "Right Sleeve", "Main Side"];
  const customizationTypeOptions = [
    "T-Shirt",
    "Hoodie",
    "Mug",
    "Cup",
    "Mobile Cover",
    "Cushion",
    "Photo Frame",
    "Tote Bag",
    "Cap",
    "Other",
  ];
  const defaultCustomizationTools = {
    images: true,
    text: true,
    fontSelection: true,
    textColor: true,
    shapes: false,
    stickers: false,
  };
 
  const [customizationEnabled, setCustomizationEnabled] = useState(false);
  const [customizationType, setCustomizationType] = useState("T-Shirt");
  const [customizationPositions, setCustomizationPositions] = useState(["Front"]);
  const [customizationMockups, setCustomizationMockups] = useState({});
  const [customizationPrintAreas, setCustomizationPrintAreas] = useState({
    Front: { x: 0.1, y: 0.1, width: 0.8, height: 0.8 },
  });
  const [customizationToolOptions, setCustomizationToolOptions] = useState(
    defaultCustomizationTools,
  );

  // Track whether admin manually edited printable areas (don't override their choices)
  const [customizationPositionsTouched, setCustomizationPositionsTouched] = useState(false);

  // Default suggested printable areas per product type (suggestions only)
  const defaultPositionsByType = {
    "T-Shirt": ["Front", "Back"],
    Hoodie: ["Front", "Back"],
    // For mugs we suggest a single generic printable surface called "Main Side"
    Mug: ["Main Side"],
    Cup: ["Front", "Back"],
    "Mobile Cover": ["Back"],
    Cushion: ["Front", "Back"],
    "Photo Frame": ["Front"],
    "Tote Bag": ["Front"],
    Cap: ["Front"],
    Other: [],
  };
 
  const specTemplates = {
    "Photo Frame": [
      "Material:",
      "Frame Color:",
      "Shape:",
      "Mount Type:",
      "Orientation:",
      "Finish:",
      "Photo Type:",
      "Usage:",
    ],
    Mug: [
      "Material:",
      "Color:",
      "Capacity:",
      "Finish:",
      "Usage:",
    ],
    "T-Shirt": [
      "Material:",
      "Color:",
      "Size:",
      "Fit:",
      "Print Type:",
      "Usage:",
    ],
    Keychain: [
      "Material:",
      "Shape:",
      "Finish:",
      "Attachment Type:",
      "Usage:",
    ],
    "Mobile Cover": [
      "Material:",
      "Color:",
      "Compatibility:",
      "Finish:",
      "Usage:",
    ],
  };
 
  const [productThumbnail, setProductThumbnail] = useState(null);

  function parseBulkSpecifications(input) {
    return String(input || "")
      .split(/\r?\n/)
      .reduce((acc, rawLine) => {
        const line = rawLine.trim();
        if (!line) return acc;

        const match = line.match(/^([^:\u2013\u2014\-]+)\s*[:\u2013\u2014\-]\s*(.*)$/);
        if (!match) return acc;

        const key = match[1].trim();
        const value = match[2].trim();
        if (!key) return acc;

        acc[key] = value;
        return acc;
      }, {});
  }

  function handleImportSpecifications() {
    const parsed = parseBulkSpecifications(bulkSpecsInput);
    if (Object.keys(parsed).length === 0) {
      window.alert(
        "Please enter specifications using the format Name: Value, one per line.",
      );
      return;
    }

    const next = {
      ...specifications,
      ...parsed,
    };
    setValue("specifications", next, { shouldDirty: true });
    setBulkSpecsInput("");
    setSelectedTemplate("");
    setShowSingleSpec(false);
  }

  function handleTemplateSelection(templateName) {
    setSelectedTemplate(templateName);
    setBulkSpecsInput(
      templateName && specTemplates[templateName]
        ? specTemplates[templateName].join("\n")
        : "",
    );
  }

  // Clean Shopify-style attribute functions
  function toggleAttribute(attributeName, enabled) {
    if (enabled) {
      setEnabledAttributes((prev) => [...prev, attributeName]);

      // Auto-select first value when attribute is enabled
      const attribute = getRelevantAttributes().find(
        (attr) => attr.name === attributeName,
      );
      const firstValue = attribute?.values?.[0];

      setSelectedAttributes((prev) => ({
        ...prev,
        [attributeName]: firstValue ? [firstValue] : [],
      }));
    } else {
      setEnabledAttributes((prev) =>
        prev.filter((name) => name !== attributeName),
      );

      setSelectedAttributes((prev) => {
        const copy = { ...prev };
        delete copy[attributeName];
        return copy;
      });
    }
  }

  function toggleAttributeValue(attrName, value) {
    setSelectedAttributes((prev) => {
      const current = prev[attrName] || [];
 
      if (current.includes(value)) {
        return {
          ...prev,
          [attrName]: current.filter((v) => v !== value),
        };
      }
 
      return {
        ...prev,
        [attrName]: [...current, value],
      };
    });
  }
 
  function toggleCustomizationPosition(position) {
    // mark that admin manually changed printable areas
    setCustomizationPositionsTouched(true);

    setCustomizationPositions((prev) => {
      const hasPosition = prev.includes(position);
      const next = hasPosition
        ? prev.filter((item) => item !== position)
        : [...prev, position];
  
      if (!hasPosition && !customizationPrintAreas[position]) {
        setCustomizationPrintAreas((current) => ({
          ...current,
          [position]: { x: 0.1, y: 0.1, width: 0.8, height: 0.8 },
        }));
      }
  
      if (hasPosition) {
        setCustomizationMockups((current) => {
          const copy = { ...current };
          delete copy[position];
          return copy;
        });
        setCustomizationPrintAreas((current) => {
          const copy = { ...current };
          delete copy[position];
          return copy;
        });
      }
  
      return next;
    });
  }
 
  function handleCustomizationMockupUpload(position, e) {
    const file = e.target.files?.[0];
    if (!file) return;
 
    setCustomizationMockups((prev) => ({
      ...prev,
      [position]: {
        preview: URL.createObjectURL(file),
        file,
        existing: false,
      },
    }));
  }

  // When admin changes customization type, suggest sensible default positions
  function handleCustomizationTypeChange(newType) {
    setCustomizationType(newType);

    // If admin already manually adjusted positions, do not override their choices
    if (customizationPositionsTouched) return;

    const defaults = defaultPositionsByType[newType] || [];

    // Apply suggested defaults (non-destructive)
    setCustomizationPositions(defaults);

    // Keep existing printAreas for positions that match, and create defaults for new ones
    setCustomizationPrintAreas((prev) => {
      const next = {};
      defaults.forEach((p) => {
        next[p] = prev[p] || { x: 0.1, y: 0.1, width: 0.8, height: 0.8 };
      });
      return next;
    });

    // Keep only mockups that match the new defaults
    setCustomizationMockups((prev) => {
      const next = {};
      defaults.forEach((p) => {
        if (prev[p]) next[p] = prev[p];
      });
      return next;
    });
  }

  function updateCustomizationPrintArea(position, field, value) {
    setCustomizationPrintAreas((prev) => ({
      ...prev,
      [position]: {
        ...(prev[position] || { x: 0.1, y: 0.1, width: 0.8, height: 0.8 }),
        [field]: Number(value),
      },
    }));
  }
 
  function handleCustomizationToolToggle(tool) {
    setCustomizationToolOptions((prev) => ({
      ...prev,
      [tool]: !prev[tool],
    }));
  }
 
  // NOTE: Removed duplicate edit mode logic - now handled in the main useEffect above
 
  // Get main categories (no parent) and subcategories (with parent)
  const getMainCategories = () => {
    return categories.filter((cat) => !cat.parentCategory);
  };

  const getSubcategories = (parentId) => {
    if (!parentId) return [];
    return categories.filter((cat) => cat.parentCategory === parentId);
  };

  // Handle main category change
  const handleMainCategoryChange = (categoryId) => {
    setSelectedMainCategory(categoryId);
    setSelectedSubcategory(""); // Reset subcategory
    setEnabledAttributes([]); // ✅ Reset for main category change
    setSelectedAttributes({}); // ✅ Reset for main category change
    // Update form category_id to main category
    setValue("category_id", categoryId);
  };

  // Handle subcategory change
  const handleSubcategoryChange = (subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
    // DON'T reset enabled attributes for subcategory change
    // The auto-enable effect will handle showing attributes for the subcategory
    // ❌ REMOVED: setValue("category_id", subcategoryId);
    // We should NOT override the main category - keep it separate!
    // Only the subcategory field should be updated
  };

  // Fetch categories and other data
  const [aboutItemPoints, setAboutItemPoints] = useState([]);
  const [aboutItemInput, setAboutItemInput] = useState("");

  // Product Image Management states
  const [colorWiseImages, setColorWiseImages] = useState({});
  const [additionalImages, setAdditionalImages] = useState([]);
  const [selectedColorImage, setSelectedColorImage] = useState(null);
  const [selectedAdditionalImage, setSelectedAdditionalImage] = useState(null);
  const [metaImage, setMetaImage] = useState(null);
  const tags = watch("tags");
  const colors = watch("colors");
  const specifications = watch("specifications") || {};
  const discountType = watch("discount_type");

  // Fetch categories and other data first
  useEffect(() => {
    console.log("📦 Fetching categories and attributes...");
    fetchLists();
  }, []);

  // Load product data AFTER categories are available (fixes race condition)
  useEffect(() => {
    if (categories.length === 0) {
      console.log("⏳ Waiting for categories to load...");
      return;
    }

    if (isEditMode && editingProduct) {
      console.log(
        "🔍 Categories loaded, now loading product data:",
        editingProduct._id,
      );
      loadProductDataForEdit(editingProduct);
    } else if (!isEditMode) {
      // Clear form for new product creation
      console.log("🆕 New product mode, clearing form");
      resetFormToDefaults();
    } else {
      // Check for localStorage edit data (fallback)
      const editId = localStorage.getItem("editProductId");
      if (editId) {
        console.log("🔍 Loading product from localStorage:", editId);
        fetchProductDataById(editId);
      }
    }
  }, [isEditMode, editingProduct, categories.length]);

  // Auto-enable first attribute when attributes load (Shopify-level UX)
  useEffect(() => {
    const attrs = getRelevantAttributes();
    console.log("🔍 Auto-enable check:", {
      relevantAttributes: attrs.length,
      enabledAttributes: enabledAttributes.length,
      isEditMode,
      firstAttribute: attrs[0]?.name,
    });

    if (attrs.length > 0 && enabledAttributes.length === 0 && !isEditMode) {
      // Only auto-enable in create mode, not edit mode
      console.log("🚀 Auto-enabling first attribute:", attrs[0].name);
      toggleAttribute(attrs[0].name, true);
    }
  }, [attributes, isEditMode]);

  // Re-fetch attributes when category changes (no page refresh needed)
  useEffect(() => {
    const categoryId = watch("category_id");
    if (categoryId) {
      console.log("📦 Category changed to:", categoryId, "- Refreshing attributes");
      fetchLists(); // Reload attributes from API
    }
  }, [watch("category_id")]); // Watch category_id for changes

  // Also refresh attributes when selectedMainCategory or selectedSubcategory state changes
  // This handles subcategory selection which also changes the form category_id
  useEffect(() => {
    const selectedCat = selectedSubcategory || selectedMainCategory;
    if (selectedCat) {
      console.log("📦 Category state changed to:", selectedCat, "- Auto-enabling attributes");
      
      // Force attributes to show for the new category
      const attrs = getRelevantAttributes();
      if (attrs.length > 0 && enabledAttributes.length === 0 && !isEditMode) {
        console.log("🚀 Auto-enabling first attribute for new category:", attrs[0].name);
        toggleAttribute(attrs[0].name, true);
      }
    }
  }, [selectedMainCategory, selectedSubcategory]);

  // DEBUG EFFECT - Log all form state in edit mode
  useEffect(() => {
    if (isEditMode) {
      const formValues = getValues();
      console.log("\n═══════════════════════════════════════════════");
      console.log("🔍 CURRENT FORM STATE (WHAT IS DISPLAYED):");
      console.log("═══════════════════════════════════════════════");
      console.log("📝 BASIC FIELDS:");
      console.log("  product_name_en:", formValues.product_name_en || "[EMPTY]");
      console.log("  description_en:", (formValues.description_en || "[EMPTY]").substring(0, 60) + "...");
      console.log("  sku:", formValues.sku || "[EMPTY]");
      console.log("  unit_price:", formValues.unit_price || "[EMPTY]");
      console.log("  shipping_cost:", formValues.shipping_cost || "[EMPTY]");
      console.log("  stock_qty:", formValues.stock_qty || "[EMPTY]");
      console.log("  tags:", formValues.tags || "[EMPTY]");
      console.log("  status:", formValues.status);
      console.log("  is_featured:", formValues.is_featured);
      console.log("  meta_title:", formValues.meta_title || "[EMPTY]");
      console.log("  meta_description:", formValues.meta_description || "[EMPTY]");
      
      console.log("\n📁 CATEGORY & ATTRIBUTES:");
      console.log("  category_id (form):", formValues.category_id || "[EMPTY]");
      console.log("  selectedMainCategory (state):", selectedMainCategory || "[EMPTY]");
      console.log("  selectedSubcategory (state):", selectedSubcategory || "[EMPTY]");
      console.log("  enabledAttributes:", enabledAttributes);
      
      console.log("\n📊 VARIATIONS & COLORS:");
      console.log("  generatedVariations:", generatedVariations.length, "items");
      console.log("  colorWiseImages colors:", Object.keys(colorWiseImages));
      
      console.log("\n🖼️ IMAGES:");
      console.log("  productThumbnail:", productThumbnail ? "✅ LOADED" : "❌ EMPTY");
      console.log("  additionalImages:", additionalImages.length, "images");
      console.log("  metaImage:", metaImage ? "✅ LOADED" : "❌ EMPTY");
      console.log("═══════════════════════════════════════════════\n");
    }
  }, [isEditMode, getValues, selectedMainCategory, selectedSubcategory, enabledAttributes, generatedVariations, colorWiseImages, productThumbnail, additionalImages, metaImage]);

  // Helper function to load product data for edit mode
  const loadProductDataForEdit = (product) => {
    console.log("🚀 LOADING PRODUCT FOR EDIT:", product._id);

    // ✅ Extract loaded category ID (handle all field name variations)
    const loadedCategoryId =
      product.category?._id || product.category || product.category_id || "";

    // ✅ CRITICAL FIX: Check if the loaded category is actually a SUBCATEGORY
    // by looking it up in the categories list to see if it has a parent
    const foundCategory = categories.find((cat) => cat._id === loadedCategoryId);

    let mainCategoryId = "";
    let subCategoryId = "";

    if (foundCategory?.parentCategory) {
      // This is a subcategory - extract parent as main category
      console.log("📊 Product category is a SUBCATEGORY, extracting parent");
      mainCategoryId =
        foundCategory.parentCategory._id || foundCategory.parentCategory;
      subCategoryId = loadedCategoryId;
    } else {
      // This is a main category
      console.log("📊 Product category is a MAIN CATEGORY");
      mainCategoryId = loadedCategoryId;
      subCategoryId = product.subcategory?._id || product.subcategory || "";
    }

    console.log("📁 Category Logic:", {
      mainCategoryId,
      subCategoryId,
      loadedCategoryId,
      foundCategory: foundCategory?.name,
      parentCategory: foundCategory?.parentCategory?.name,
    });

    reset({
      product_name_en: product.product_name_en || product.name || "",
      description_en: product.description_en || product.description || "",
      category_id: mainCategoryId, // Main category (has no parent)
      sku: product.sku || "",
      unit_price: product.price || product.unit_price || "",
      unit: product.unit || "pcs",
      discount_type: product.discount_type || "flat",
      discount_amount: product.discount_amount || "",
      tax_percent: product.tax_percent || "",
      tax_calculation: product.tax_calculation || "include",
      shipping_cost:
        product.shipping_cost !== undefined
          ? String(product.shipping_cost)
          : "",
      shipping_multiply_quantity: product.shipping_multiply_quantity || false,
      stock_qty: product.stock_qty || product.stock || "",
      min_order_qty: product.min_order_qty || "1",
      max_order_qty: product.max_order_qty || "",
      tags: Array.isArray(product.tags)
        ? product.tags.map((t) => String(t).replace(/^"+|"+$/g, ""))
        : [],
      specifications: product.specifications || {},
    });

    // ✅ Use variations directly from product as-is (the table handles field name variations)
    console.log("🔍 PRODUCT.VARIATIONS DEBUG:", {
      variationsExists: !!product.variations,
      variationsLength: product.variations?.length,
      variationsArray: product.variations,
      firstVariation: product.variations?.[0],
      firstVariationKeys: Object.keys(product.variations?.[0] || {}),
    });

    // 🔍 DEEP DIAGNOSTIC - Check if properties are non-enumerable
    if (product.variations?.[0]) {
      const firstVar = product.variations[0];
      console.log("🔍 DEEP VARIATION DIAGNOSTIC:", {
        type: typeof firstVar,
        constructor: firstVar?.constructor?.name,
        json: JSON.stringify(firstVar),
        ownPropertyNames: Object.getOwnPropertyNames(firstVar),
        propertyNames: Object.keys(firstVar),
        bracketAccessTest: {
          id: firstVar?.id,
          name: firstVar?.name,
          price: firstVar?.price,
          sku: firstVar?.sku,
          stock: firstVar?.stock,
        },
      });
    }

    setGeneratedVariations(product.variations || []);

    // ✅ FIXED: Handle all field name variations for color images
    const colorImages = 
      product.color_wise_images ||
      product.colorWiseImages ||
      product.colorImages ||
      {};

    setColorWiseImages(
      Object.keys(colorImages).length > 0
        ? Object.fromEntries(
            Object.entries(colorImages).map(([color, url]) => [
              color.toLowerCase(),
              { preview: url, existing: true },
            ])
          )
        : {}
    );

    console.log("🎨 Color images loaded:", {
      count: Object.keys(colorImages).length,
      colors: Object.keys(colorImages),
    });

    setProductThumbnail(
      product.thumbnail
        ? { preview: product.thumbnail, existing: true }
        : null
    );

    setAdditionalImages(
      Array.isArray(product.additional_images)
        ? product.additional_images.map((url) => ({
            preview: url,
            existing: true,
          }))
        : []
    );

    setMetaImage(
      product.meta_image
        ? { preview: product.meta_image, existing: true }
        : null
    );
 
    const customizationData = product.customization || {};
    setCustomizationEnabled(Boolean(customizationData.enabled));
    setCustomizationType(customizationData.type || "T-Shirt");
    setCustomizationPositions(
      Array.isArray(customizationData.printAreas) && customizationData.printAreas.length > 0
        ? customizationData.printAreas
        : ["Front"],
    );
    setCustomizationMockups(
      customizationData.mockups
        ? Object.fromEntries(
            Object.entries(customizationData.mockups).map(([position, url]) => [
              position,
              { preview: url, existing: true },
            ]),
          )
        : {},
    );
    setCustomizationPrintAreas(
      customizationData.printAreaMap || {
        Front: { x: 0.1, y: 0.1, width: 0.8, height: 0.8 },
      },
    );
    setCustomizationToolOptions(
      customizationData.tools || defaultCustomizationTools,
    );
 
    console.log("✅ All edit states loaded successfully");
  };

  // Helper function to fetch product by ID
  const fetchProductDataById = async (editId) => {
    try {
      console.log("🔍 Fetching product data by ID:", editId);
      const productData = await apiService.getProduct(editId);
      console.log("✅ Product data fetched:", productData);
      loadProductDataForEdit(productData);
    } catch (error) {
      console.error("❌ Error fetching product data:", error);
      // Try localStorage fallback
      const storedData = localStorage.getItem(editId);
      if (storedData) {
        const product = JSON.parse(storedData);
        loadProductDataForEdit(product);
      }
    }
  };

  // Helper function to reset form to defaults for new product
  const resetFormToDefaults = () => {
    reset({
      product_name_en: "",
      description_en: "",
      category_id: "",
      product_type: "physical",
      sku: "",
      unit: "pcs",
      tags: [],
      unit_price: "",
      min_order_qty: "1",
      stock_qty: "",
      discount_type: "flat",
      discount_amount: "",
      tax_percent: "",
      tax_calculation: "include",
      shipping_cost: "",
      colors: [],
      video_link: "",
      meta_title: "",
      meta_description: "",
      indexing_option: "index",
      max_snippet: "-1",
      max_video_preview: "-1",
      max_image_preview: "large",
      shipping_multiply_quantity: false,
      variations: [],
      specifications: {},
      status: "active",
      is_featured: false,
      created_at: "",
      updated_at: "",
      vendor_id: "",
      approved: true,
      thumbnail: "",
      additional_images: [],
      color_wise_images: {},
      meta_image: "",
      customization: {
        enabled: false,
        type: "T-Shirt",
        printAreas: ["Front"],
        mockups: {},
        printAreaMap: {
          Front: { x: 0.1, y: 0.1, width: 0.8, height: 0.8 },
        },
        tools: defaultCustomizationTools,
      },
    });
 
    // Clear all state variables
    setGeneratedVariations([]);
    setColorWiseImages({});
    setProductThumbnail(null);
    setAdditionalImages([]);
    setMetaImage(null);
    setImagePreviews([]);
    setImages([]);
    setEnabledAttributes([]);
    setSelectedAttributes({});
    setCustomizationEnabled(false);
    setCustomizationType("T-Shirt");
    setCustomizationPositions(["Front"]);
    setCustomizationMockups({});
    setCustomizationPrintAreas({
      Front: { x: 0.1, y: 0.1, width: 0.8, height: 0.8 },
    });
    setCustomizationToolOptions(defaultCustomizationTools);

    console.log("✅ Form reset to defaults");
  };

  async function fetchLists() {
    try {
      const [categoriesData, attributesData] = await Promise.all([
        apiService.getCategories(),
        axios.get(`${apiService.baseURL}/attributes`),
      ]);

      // Set real data from API
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);

      // Handle different API response formats
      const attrs = attributesData.data?.data || [];

      setAttributes(Array.isArray(attrs) ? attrs : []);
      console.log("📋 Loaded attributes:", attrs.length, "attributes");
      console.log("📋 Attributes:", attrs);
      console.log("📋 Full API response:", attributesData);
    } catch (err) {
      console.error("Failed to load lists from API:", err);
      alert(
        "Failed to load categories or attributes. Please check if the backend server is running.",
      );

      // Set empty arrays instead of mock data
      setCategories([]);
      setAttributes([]);
    }
  }

  function generateSKU() {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substr(2, 5).toUpperCase();
    const code = `SKU-${timestamp}-${random}`;
    setValue("sku", code);
    return code;
  }

  // Get relevant attributes based on selected category
  function getRelevantAttributes() {
    // Try to get category from form first, then fall back to state
    let selectedCategoryId = watch("category_id");
    if (!selectedCategoryId) {
      // Fallback to state if form value is missing
      selectedCategoryId = selectedSubcategory || selectedMainCategory;
    }

    const selectedCategory = categories.find(
      (cat) => cat._id === selectedCategoryId || cat.id === selectedCategoryId,
    );

    // Ensure attributes is always an array
    const attributesArray = Array.isArray(attributes) ? attributes : [];

    console.log("🔍 getRelevantAttributes debug:", {
      formCategoryId: watch("category_id"),
      stateCategoryId: selectedSubcategory || selectedMainCategory,
      resolvedCategoryId: selectedCategoryId,
      selectedCategory: selectedCategory?.name,
      selectedCategoryParent: selectedCategory?.parentCategory,
      totalAttributes: attributesArray.length,
      allProductsAttrs: attributesArray.filter(
        (attr) => attr.category === "All Products" && attr.active !== false,
      ),
      activeAttrs: attributesArray.filter((attr) => attr.active !== false),
    });

    if (!selectedCategory) {
      console.log("📋 No category selected, returning empty array");
      return [];
    }

    // If it's a subcategory (has parentCategory), we need to find parent name
    // because attributes are matched to parent category name
    let categoryName = selectedCategory.name?.toLowerCase() || "";
    
    if (selectedCategory.parentCategory) {
      // It's a subcategory - find parent and use parent name for matching
      const parentCategory = categories.find(
        (cat) => cat._id === selectedCategory.parentCategory || 
                 cat.id === selectedCategory.parentCategory
      );
      if (parentCategory) {
        categoryName = parentCategory.name?.toLowerCase() || "";
        console.log("📋 Using parent category name:", parentCategory.name, "for subcategory:", selectedCategory.name);
      }
    }

    // Return ALL relevant attributes for this category
    const relevantAttributes = attributesArray.filter((attr) => {
      const attrCategory = (attr.category || "").toLowerCase().trim();
      const selectedCat = categoryName.trim();

      return (
        attr.active !== false &&
        (attrCategory === selectedCat || // exact match
          attrCategory === "all products") // global
      );
    });

    // Remove duplicates based on name
    const uniqueAttributes = relevantAttributes.filter(
      (attr, index, self) =>
        index === self.findIndex((a) => a.name === attr.name),
    );

    console.log(
      "📋 Final relevant attributes:",
      uniqueAttributes.length,
      uniqueAttributes,
    );

    // Additional debug for troubleshooting
    console.log("🎯 FINAL RESULT CHECK:", {
      isEmpty: uniqueAttributes.length === 0,
      count: uniqueAttributes.length,
      attributes: uniqueAttributes.map((a) => ({
        name: a.name,
        category: a.category,
        active: a.active,
      })),
    });

    return uniqueAttributes;
  }

  // Clean Shopify-style variation generation
  function generateVariations() {
    console.log("🎨 GENERATE VARIATIONS CALLED");
    console.log("🎨 selectedAttributes:", selectedAttributes);
    console.log("🎨 colorWiseImages:", colorWiseImages);

    const keys = Object.keys(selectedAttributes);
    console.log("🎨 Attribute keys:", keys);

    if (keys.length === 0) {
      alert("Select at least one attribute");
      return;
    }

    let combinations = [{}];
    console.log("🎨 Initial combinations:", combinations);

    keys.forEach((key) => {
      const values = selectedAttributes[key];
      console.log(`🎨 Processing key: ${key}, values:`, values);

      if (!values || values.length === 0) {
        console.log(`🎨 Skipping ${key} - no values`);
        return;
      }

      const newCombos = [];

      combinations.forEach((combo) => {
        values.forEach((val) => {
          const newCombo = {
            ...combo,
            [key.toLowerCase()]: val, // ✅ Convert attribute name to lowercase
          };
          console.log(`🎨 New combo for ${key}:`, newCombo);
          newCombos.push(newCombo);
        });
      });

      combinations = newCombos;
      console.log(`🎨 Combinations after ${key}:`, combinations);
    });

    console.log("🎨 Final combinations:", combinations);

    const mainSKU = watch("sku") || "";
    console.log("🎨 Main SKU:", mainSKU);

    const variations = combinations.map((combo, index) => {
      const color = combo["color"]; // ✅ Now this will work - color is lowercase
      const colorImage = colorWiseImages[color?.toLowerCase()]?.preview || "";

      console.log(`🎨 Creating variation ${index}:`, {
        combo,
        color,
        colorImage,
      });

      return {
        id: index + 1,
        name: Object.entries(combo)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", "),
        ...combo,
        price: "",
        stock: "",
        sku: `${mainSKU}-${Object.values(combo).join("-").toUpperCase()}`,
        image: colorImage, // ✅ Now color will be defined
      };
    });

    console.log("🎨 Final variations:", variations);
    setGeneratedVariations(variations);
  }

  // Update variation field
  function updateVariation(index, field, value) {
    const updatedVariations = [...generatedVariations];
    updatedVariations[index][field] = value;
    setGeneratedVariations(updatedVariations);
  }

  // Get color hex for display
  function getColorHex(color) {
    const colorMap = {
      Red: "#ef4444",
      "Dark Green": "#166534",
      Black: "#000000",
      Blue: "#2563eb",
      White: "#f3f4f6",
      Yellow: "#eab308",
    };
    return colorMap[color] || "#6b7280";
  }

  // Strip HTML tags from text
  const stripHtml = (html = "") => {
    return html.replace(/<[^>]*>/g, "").trim();
  };

  // Auto-fill meta fields based on product details
  const handleAutoFillMeta = () => {
    const name =
      watch("product_name_en") ||
      watch("name") ||
      "";

    const description =
      watch("description_en") ||
      watch("description") ||
      "";

    setValue("meta_title", name.substring(0, 60));

    setValue(
      "meta_description",
      stripHtml(description).substring(0, 160)
    );
  };

  // New handler functions for product specifications
  function addOccasionFromInput() {
    const occasion = occasionInput.trim();
    if (occasion && !selectedOccasions.includes(occasion)) {
      setSelectedOccasions((prev) => [...prev, occasion]);
      setOccasionInput("");
      setValue("occasions", [...selectedOccasions, occasion]);
    }
  }

  function handleOccasionInputKeyPress(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addOccasionFromInput();
    }
  }

  function deleteOccasion(occasionToDelete) {
    const updated = selectedOccasions.filter((o) => o !== occasionToDelete);
    setSelectedOccasions(updated);
    setValue("occasions", updated);
  }

  function deleteProductColor(colorToDelete) {
    const updated = selectedProductColors.filter((c) => c !== colorToDelete);
    setSelectedProductColors(updated);
    setValue("colors", updated);
  }

  function addAboutItemPoint() {
    const point = aboutItemInput.trim();
    if (point && !aboutItemPoints.includes(point)) {
      setAboutItemPoints((prev) => [...prev, point]);
      setAboutItemInput("");
      setValue("about_item", aboutItemPoints.concat(point).join("\n"));
    }
  }

  function handleAboutItemInputKeyPress(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addAboutItemPoint();
    }
  }

  function deleteAboutItemPoint(pointToDelete) {
    const updated = aboutItemPoints.filter((p) => p !== pointToDelete);
    setAboutItemPoints(updated);
    setValue("about_item", updated.join("\n"));
  }

  // Product Image Management Functions
  function handleProductThumbnailUpload(e) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductThumbnail({
          file,
          preview: reader.result,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  function handleColorImageUpload(color, e) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setColorWiseImages((prev) => ({
          ...prev,
          [color.toLowerCase()]: {
            // ✅ CRITICAL FIX: Normalize to lowercase
            file,
            preview: reader.result,
            name: file.name,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  }

  function handleAdditionalImagesUpload(e) {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setAdditionalImages((prev) => [...prev, ...newImages]);
  }

  function removeProductThumbnail() {
    setProductThumbnail(null);
  }

  function removeColorImage(color) {
    const key = color.toLowerCase(); // Normalize to lowercase
    setColorWiseImages((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
    if (selectedColorImage === color) {
      setSelectedColorImage(null);
    }
  }

  function removeAdditionalImage(index) {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    if (selectedAdditionalImage === index) {
      setSelectedAdditionalImage(null);
    }
  }

  function selectColorImage(color) {
    setSelectedColorImage(color);
  }

  function selectAdditionalImage(index) {
    setSelectedAdditionalImage(index);
  }

  function handleMetaImageUpload(e) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMetaImage({
          file,
          preview: reader.result,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  function removeMetaImage() {
    setMetaImage(null);
  }

  function onSelectImages(e) {
    const files = Array.from(e.target.files);
    setImages(files);
    const previews = files.map((f) => URL.createObjectURL(f));
    setImagePreviews(previews);
  }

  function removeImage(index) {
    const nextFiles = images.slice();
    nextFiles.splice(index, 1);
    setImages(nextFiles);
    const nextPreviews = imagePreviews.slice();
    nextPreviews.splice(index, 1);
    setImagePreviews(nextPreviews);
  }

  function handleAddTagFromInput(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = e.target.value.trim();

      // Split by comma and process multiple tags
      const newTags = val
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag && !(tags && tags.includes(tag)));

      if (newTags.length > 0) {
        const next = Array.from(new Set([...(tags || []), ...newTags]));
        setValue("tags", next, { shouldValidate: true, shouldDirty: true });
        e.target.value = "";
      }
    }
  }

  function handleDeleteTag(tagToDelete) {
    const next = (tags || []).filter((t) => t !== tagToDelete);
    setValue("tags", next, { shouldValidate: true, shouldDirty: true });
  }

  function handleAddSpecification(name, value) {
    const specName = String(name || "").trim();
    const specValue = String(value || "").trim();
    if (!specName || !specValue) return;

    const next = {
      ...specifications,
      [specName]: specValue,
    };
    setValue("specifications", next, { shouldDirty: true });
    setNewSpecName("");
    setNewSpecValue("");
  }

  function handleSpecificationChange(key, field, value) {
    if (!key) return;
    const next = { ...specifications };
    if (field === "name") {
      const existingValue = next[key];
      delete next[key];
      next[value] = existingValue;
    } else {
      next[key] = value;
    }
    setValue("specifications", next, { shouldDirty: true });
  }

  function handleDeleteSpecification(key) {
    const next = { ...specifications };
    delete next[key];
    setValue("specifications", next, { shouldDirty: true });
  }

  function onSelectThumbnail(e) {
    const file = e.target.files[0];
    if (file) {
      setThumbnailPreview(URL.createObjectURL(file));
    }
  }

  function removeThumbnail() {
    setThumbnailPreview(null);
  }

  async function onSubmit(data) {
    if (loading) {
      console.log("🚫 Already submitting, preventing double submission");
      return;
    }
    setLoading(true);
    try {
      console.log("📝 Submitting form data:", data);
      console.log("🔄 Edit mode:", isEditMode);
      console.log("📦 Editing product:", editingProduct);

      // Skip FormData building - let API service handle it

      // Save product using API

      try {
        // Check if editing existing product or creating new one
        const editId = localStorage.getItem("editProductId");
        const isLocationEditMode = isEditMode && editingProduct;

        // Function to get changed fields using dirtyFields
        function getChangedFields(newData, originalData, dirtyFields) {
          const changed = {};

          Object.keys(dirtyFields).forEach((key) => {
            if (dirtyFields[key]) {
              // Field is dirty, include it in the update
              changed[key] = newData[key];
            }
          });

          // Handle special cases for complex objects
          if (dirtyFields.variations && generatedVariations) {
            changed.variations = generatedVariations;
            changed.has_variations = generatedVariations.length > 0;
          }

          if (Object.keys(colorWiseImages).length > 0) {
            changed.color_wise_images = colorWiseImages;
          }
 
          const originalSpecs = originalData?.specifications || {};
          if (
            data.specifications &&
            JSON.stringify(data.specifications) !==
              JSON.stringify(originalSpecs)
          ) {
            changed.specifications = data.specifications;
          }
 
          const originalCustomization = originalData?.customization || {};
          const nextCustomization = {
            enabled: customizationEnabled,
            type: customizationType,
            printAreas: customizationPositions,
            mockups: Object.fromEntries(
              Object.entries(customizationMockups)
                .filter(([_, item]) => item?.existing)
                .map(([position, item]) => [position, item.preview]),
            ),
            printAreaMap: customizationPrintAreas,
            tools: customizationToolOptions,
          };

          if (
            JSON.stringify(originalCustomization) !==
            JSON.stringify(nextCustomization)
          ) {
            changed.customization = nextCustomization;
          }

          return changed;
        }
 
        // Prepare full product data first
        const fullProductData = {
          product_name_en: data.product_name_en || "",
          description_en: data.description_en || "",
          category_id: selectedMainCategory || "",
          category: selectedMainCategory || "",
          subcategory: selectedSubcategory || null,
          sku: data.sku || "",
          unit_price: data.unit_price || "",
          discount_type: data.discount_type || "flat",
          discount_amount: data.discount_amount || "",
          tax_percent: data.tax_percent || "",
          tax_calculation: data.tax_calculation || "include",
          shipping_cost: data.shipping_cost || "",
          shipping_multiply_quantity: data.shipping_multiply_quantity || false,
          stock_qty: data.stock_qty || "",
          min_order_qty: data.min_order_qty || "1",
          max_order_qty: data.max_order_qty || "",
          tags: data.tags || [],
          status: data.status || "active",
          is_featured: data.is_featured || false,
          has_variations:
            (generatedVariations && generatedVariations.length > 0) || false,
          variations: generatedVariations || [],
          video_link: data.video_link || "",
          meta_title: data.meta_title || "",
          meta_description: data.meta_description || "",
          indexing_option: data.indexing_option || "index",
          max_snippet: data.max_snippet || "-1",
          max_video_preview: data.max_video_preview || "-1",
          max_image_preview: data.max_image_preview || "large",
          specifications: data.specifications || {},
          customization: {
            enabled: customizationEnabled,
            type: customizationType,
            printAreas: customizationPositions,
            mockups: Object.fromEntries(
              Object.entries(customizationMockups)
                .filter(([_, item]) => item?.existing)
                .map(([position, item]) => [position, item.preview]),
            ),
            printAreaMap: customizationPrintAreas,
            tools: customizationToolOptions,
          },
          // Strip out base64 preview data to reduce payload size - keep only color references
          color_wise_images: Object.keys(colorWiseImages || {}).reduce((acc, color) => {
            // Only include colors that have images, with empty metadata to avoid large base64 data
            acc[color] = {};
            return acc;
          }, {}),
        };

        let productData;

        if (isLocationEditMode || editId) {
          // Edit mode: send only changed fields
          productData = getChangedFields(
            fullProductData,
            originalProduct,
            dirtyFields,
          );

          console.log("\n═══════════════════════════════════════════════");
          console.log("✏️ EDIT MODE - SUBMITTING UPDATE:");
          console.log("═══════════════════════════════════════════════");
          console.log("📦 Fields being updated (dirty fields):", Object.keys(productData));
          console.log("📝 Data to update:", productData);
          console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
          console.log("📋 Full product data structure:");
          console.log({
            product_name: productData.product_name_en || "[KEEPING EXISTING]",
            category_id: productData.category_id || "[KEEPING EXISTING]",
            subcategory: productData.subcategory || "[KEEPING EXISTING]",
            sku: productData.sku || "[KEEPING EXISTING]",
            unit_price: productData.unit_price || "[KEEPING EXISTING]",
            shipping_cost: productData.shipping_cost || "[KEEPING EXISTING]",
            variations: productData.variations ? productData.variations.length + " variations" : "[KEEPING EXISTING]",
            color_images: productData.color_wise_images ? Object.keys(productData.color_wise_images).length + " colors" : "[KEEPING EXISTING]",
            images: "See 'files' object for new uploads",
          });
          console.log("═══════════════════════════════════════════════\n");

          if (Object.keys(productData).length === 0) {
            alert(
              "No changes detected. Please make some changes before updating.",
            );
            setLoading(false);
            return;
          }

          console.log("🔧 Partial update data:", productData);
          console.log("🔧 Dirty fields:", dirtyFields);
        } else {
          // Create mode: send all data
          productData = fullProductData;
          
          console.log("\n═══════════════════════════════════════════════");
          console.log("✨ CREATE MODE - SUBMITTING NEW PRODUCT:");
          console.log("═══════════════════════════════════════════════");
          console.log("📦 Product name:", productData.product_name_en);
          console.log("📦 Category ID:", productData.category_id);
          console.log("📦 Subcategory ID:", productData.subcategory);
          console.log("📦 SKU:", productData.sku);
          console.log("📦 Price:", productData.unit_price);
          console.log("📦 Shipping cost:", productData.shipping_cost);
          console.log("📦 Variations:", productData.variations ? productData.variations.length + " items" : "0");
          console.log("📦 Color images:", productData.color_wise_images ? Object.keys(productData.color_wise_images).length + " colors" : "0");
          console.log("🖼️  Will upload images separately (see 'files' object)");
          console.log("═══════════════════════════════════════════════\n");
        }

        console.log("🔄 VARIATIONS DEBUG:");
        console.log("🔄 generatedVariations:", generatedVariations);
        console.log("🔄 variations length:", generatedVariations?.length || 0);
        console.log(
          "🔄 has_variations:",
          (generatedVariations && generatedVariations.length > 0) || false,
        );
        console.log("🔄 productData.variations:", productData.variations);

        // Prepare files for upload - ONLY upload new files, not existing URLs
        const files = {};

        // Thumbnail - only upload if new file exists
        if (productThumbnail?.file) {
          files.thumbnail = productThumbnail.file;
          console.log("📤 Uploading new thumbnail");
        }

        // Meta Image - only upload if new file exists
        if (metaImage?.file) {
          files.meta_image = metaImage.file;
          console.log("📤 Uploading new meta image");
        }

        // Additional Images - only upload new files
        const additionalImageFiles = [];
        if (additionalImages && Array.isArray(additionalImages)) {
          for (const img of additionalImages) {
            if (img?.file) {
              additionalImageFiles.push(img.file);
            }
          }
        }
        if (additionalImageFiles.length > 0) {
          files.additional_images = additionalImageFiles;
          console.log("📤 Uploading", additionalImageFiles.length, "new additional images");
        }

        // Color Images - only upload if file exists
        const colorImages = {};
        for (const color of Object.keys(colorWiseImages)) {
          const item = colorWiseImages[color];
          if (item?.file) {
            colorImages[color] = item.file;
            console.log("📤 Uploading new color image for", color);
          }
        }
        if (Object.keys(colorImages).length > 0) {
          files.color_images = colorImages;
          console.log("📤 Uploading color images:", Object.keys(colorImages));
        }

        // Customization mockup uploads (dynamic positions) — send as individual fields
        const customizationMockupFiles = {};
        for (const position of Object.keys(customizationMockups || {})) {
          const mock = customizationMockups[position];
          if (mock?.file) {
            const safePosition = String(position || '')
              .toLowerCase()
              .replace(/\s+/g, "_");
            // api service expects files.customization_mockups to be an object keyed by position (e.g., "Front"),
            // it will append form fields named `customization_mockup_${safePosition}` when sending FormData.
            customizationMockupFiles[position] = mock.file;
            console.log(
              "📤 Uploading customization mockup:",
              position,
              `customization_mockup_${safePosition}`,
            );
          }
        }
        if (Object.keys(customizationMockupFiles).length > 0) {
          // attach at top-level so api service can append each file field
          files.customization_mockups = customizationMockupFiles;
        }
 
 
        // Save via API - simplified approach
        let savedProduct;
        console.log("API CALL: Starting product save operation");
        console.log("API CALL: productData keys:", Object.keys(productData));
        console.log("API CALL: files keys:", Object.keys(files));

        console.log("\n═══════════════════════════════════════════════");
        console.log("🚀 FINAL API CALL SUMMARY:");
        console.log("═══════════════════════════════════════════════");
        console.log("📤 MODE:", isLocationEditMode || editId ? "UPDATE EXISTING" : "CREATE NEW");
        console.log("📤 Product Data Fields:", Object.keys(productData));
        console.log("📤 Images to Upload:", Object.keys(files));
        if (files.thumbnail) console.log("   ✅ New thumbnail");
        if (files.additional_images) console.log("   ✅ New additional images:", files.additional_images.length);
        if (files.meta_image) console.log("   ✅ New meta image");
        if (files.color_images) console.log("   ✅ New color images:", Object.keys(files.color_images));
        console.log("═══════════════════════════════════════════════\n");

        try {
          if (isLocationEditMode || editId) {
            // Update existing product
            let productId = isLocationEditMode ? editingProduct._id : editId;

            // Ensure productId is a string (MongoDB ObjectId might be an object)
            if (productId && typeof productId === "object") {
              productId = productId.toString();
            }

            // Fallback to string conversion if needed
            productId = String(productId || "");

            // Validate that we have a valid product ID (24-char hex string or 12-char string)
            if (
              !productId ||
              (productId.length !== 24 && productId.length !== 12)
            ) {
              throw new Error(
                `Invalid product ID format: ${productId}. Expected 24-char hex or 12-char string.`,
              );
            }

            console.log("API CALL: Updating existing product:", productId);
            console.log("DEBUG - isLocationEditMode:", isLocationEditMode);
            console.log("DEBUG - editId:", editId);
            console.log("DEBUG - editingProduct._id:", editingProduct?._id);
            console.log("DEBUG - productId type:", typeof productId);
            console.log("DEBUG - final productId:", productId);
            savedProduct = await apiService.updateProductWithFiles(
              productId,
              productData,
              files,
            );
            console.log("\n✅ API CALL: Product updated successfully");
            console.log("Saved Product ID:", savedProduct._id);
            console.log("Updated fields in response:", Object.keys(savedProduct).slice(0, 10) + "...");
          } else {
            // Create new product
            console.log("API CALL: Creating new product...");
            savedProduct = await apiService.createProductWithFiles(
              productData,
              files,
            );
            console.log("\n✅ API CALL: Product created successfully");
            console.log("New Product ID:", savedProduct._id);
            console.log("Response fields:", Object.keys(savedProduct).slice(0, 10) + "...");
          }
        } catch (error) {
          console.error("API CALL ERROR:", error);
          throw error;
        }

        // Clear edit data if editing
        if (isLocationEditMode) {
          // Location state will be cleared on navigation
        } else if (editId) {
          localStorage.removeItem("editProductId");
          localStorage.removeItem("editProductData");
        }

        // Show success message
        const action = isLocationEditMode || editId ? "updated" : "saved";
        alert(`Product ${action} successfully!`);

        // Redirect to product details page
        const productId =
          savedProduct.product?._id ||
          savedProduct.product?.id ||
          savedProduct._id ||
          savedProduct.id;
        navigate(`/dashboard/products/${productId}`, { replace: true });
      } catch (apiError) {
        console.error("FRONTEND CATCH 1: API Error:", apiError);
        console.error("FRONTEND CATCH 2: Error message:", apiError.message);
        console.error("FRONTEND CATCH 3: Error stack:", apiError.stack);

        // Show the actual API error instead of falling back to localStorage
        const errorMessage = apiError.message || "Unknown API error";
        alert(
          `API Error: ${errorMessage}\n\nPlease check the console for more details.`,
        );
        setLoading(false);
        return; // Don't proceed to localStorage fallback
      }
    } catch (err) {
      console.error("Detailed error:", err);
      alert(`Failed to add product: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Back Button */}
      <Box sx={{ mb: { xs: 2, md: 2 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/product-list")}
          sx={{
            textTransform: "none",
            color: "#1976d2",
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Back to Product List
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 2, md: 3 },
          borderRadius: 3,
          background: "white",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
          spacing={{ xs: 2, sm: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Inventory2Icon
              sx={{
                fontSize: { xs: 28, sm: 32 },
                color: "#2563eb",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
            >
              {isEditMode ? "Edit Product" : "Add New Product"}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          background: "white",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "#e2e8f0" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab
              label="English (EN)"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                color: activeTab === 0 ? "#2563eb" : "#64748b",
                "&.Mui-selected": { color: "#2563eb" },
              }}
            />
          </Tabs>
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 1, sm: 1.5 },
                  color: "#374151",
                  fontSize: { xs: "0.875rem", sm: "0.875rem" },
                }}
              >
                Product Name (EN) <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <Controller
                name="product_name_en"
                control={control}
                rules={{ required: "Product name is required", maxLength: 255 }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="Enter product name"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          borderColor: "#2563eb",
                          boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: { xs: 1.5, sm: 2.5 },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 1, sm: 1.5 },
                  color: "#374151",
                  fontSize: { xs: "0.875rem", sm: "0.875rem" },
                }}
              >
                Description (EN) <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <Controller
                name="description_en"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field, fieldState }) => (
                  <Box
                    sx={{
                      "& .ql-container": {
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      },
                      "& .ql-toolbar": {
                        fontSize: { xs: "0.875rem", sm: "0.875rem" },
                      },
                    }}
                  >
                    <ReactQuill
                      ref={quillRef}
                      value={field.value}
                      onChange={field.onChange}
                      theme="snow"
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ color: [] }, { background: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          ["clean"],
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "strike",
                        "color",
                        "background",
                        "list",
                        "bullet",
                        "align",
                      ]}
                      style={{
                        height: 250,
                        marginBottom: 20,
                      }}
                    />
                    {fieldState.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ mb: { xs: 3, md: 4 } }}
            >
              <Inventory2Icon
                sx={{
                  fontSize: { xs: 20, sm: 24 },
                  color: "#2563eb",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: { xs: "1.125rem", sm: "1.25rem" },
                }}
              >
                General Setup
              </Typography>
            </Stack>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Category <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedMainCategory}
                    onChange={(e) => handleMainCategoryChange(e.target.value)}
                    displayEmpty
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          borderColor: "#2563eb",
                          boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select main category
                    </MenuItem>
                    {getMainCategories().map((c) => (
                      <MenuItem key={c._id || c.id} value={c._id || c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Subcategory <span style={{ color: "#9ca3af" }}>(Optional)</span>
                </Typography>
                <FormControl fullWidth disabled={!selectedMainCategory}>
                  <Select
                    value={selectedSubcategory}
                    onChange={(e) => handleSubcategoryChange(e.target.value)}
                    displayEmpty
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          borderColor: "#2563eb",
                          boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      {selectedMainCategory
                        ? "Select subcategory (optional)"
                        : "Select main category first"}
                    </MenuItem>
                    {selectedMainCategory &&
                      getSubcategories(selectedMainCategory).map((sub) => (
                        <MenuItem key={sub._id || sub.id} value={sub._id || sub.id}>
                          {sub.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Product Type <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <Controller
                  name="product_type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      {...field}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                      }}
                    >
                      <MenuItem value="physical">Physical</MenuItem>
                      <MenuItem value="digital">Digital</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Product SKU <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <Controller
                  name="sku"
                  control={control}
                  rules={{ required: "SKU is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="Ex: 161183"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={generateSKU}
                            size="small"
                            sx={{ color: "#2563eb" }}
                          >
                            <AutorenewIcon />
                          </IconButton>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Unit
                </Typography>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      {...field}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                      }}
                    >
                      <MenuItem value="set">set</MenuItem>
                      <MenuItem value="pcs">pcs</MenuItem>
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="gm">gm</MenuItem>
                      <MenuItem value="ltr">ltr</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Search Tags
                </Typography>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        placeholder="Enter tag"
                        fullWidth
                        onKeyDown={handleAddTagFromInput}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              borderColor: "#cbd5e1",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              borderColor: "#2563eb",
                              boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                            },
                          },
                        }}
                      />
                      <Box sx={{ mt: 2 }}>
                        {(field.value || []).map((t) => (
                          <Chip
                            key={t}
                            label={t}
                            onDelete={() => handleDeleteTag(t)}
                            sx={{
                              mr: 1,
                              mb: 1,
                              backgroundColor: "#e0f2fe",
                              color: "#0369a1",
                              "&:hover": {
                                backgroundColor: "#bae6fd",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Paper>

        {/* Specifications Section */}
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ mb: { xs: 3, md: 4 } }}
            >
              <Inventory2Icon
                sx={{
                  fontSize: { xs: 20, sm: 24 },
                  color: "#2563eb",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: { xs: "1.125rem", sm: "1.25rem" },
                }}
              >
                Specifications
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Stack spacing={1}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#475569", fontWeight: 600 }}
                  >
                    Paste specifications (one per line)
                  </Typography>
                  <TextField
                    multiline
                    minRows={6}
                    value={bulkSpecsInput}
                    onChange={(e) => {
                      setBulkSpecsInput(e.target.value);
                      setSelectedTemplate("");
                    }}
                    placeholder={
                      "Material: MDF Wood + Acrylic Front\nFrame Color: Black\nShape: Rectangle"
                    }
                    fullWidth
                    sx={{
                      backgroundColor: "#f8fafc",
                      borderRadius: 2,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", lineHeight: 1.6 }}
                  >
                    Paste each specification in the format <strong>Name: Value</strong>. One specification per line.
                  </Typography>
                </Stack>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <FormControl fullWidth>
                  <InputLabel id="spec-template-label">Choose Template</InputLabel>
                  <Select
                    labelId="spec-template-label"
                    value={selectedTemplate}
                    label="Choose Template"
                    onChange={(e) => handleTemplateSelection(e.target.value)}
                    sx={{ backgroundColor: "#f8fafc", borderRadius: 2 }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {Object.keys(specTemplates).map((templateName) => (
                      <MenuItem key={templateName} value={templateName}>
                        {templateName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleImportSpecifications}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    py: 1.75,
                  }}
                >
                  Import Specifications
                </Button>
                <Button
                  variant={showSingleSpec ? "outlined" : "text"}
                  onClick={() => setShowSingleSpec((prev) => !prev)}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    justifyContent: "flex-start",
                    color: showSingleSpec ? "#1f2937" : "#2563eb",
                  }}
                >
                  {showSingleSpec ? "Hide Add Single Specification" : "+ Add Single Specification"}
                </Button>
              </Grid>
            </Grid>

            {showSingleSpec && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    value={newSpecName}
                    onChange={(e) => setNewSpecName(e.target.value)}
                    label="Specification Name"
                    placeholder="Material"
                    fullWidth
                    sx={{
                      backgroundColor: "#f8fafc",
                      borderRadius: 2,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    value={newSpecValue}
                    onChange={(e) => setNewSpecValue(e.target.value)}
                    label="Specification Value"
                    placeholder="100% Cotton"
                    fullWidth
                    sx={{
                      backgroundColor: "#f8fafc",
                      borderRadius: 2,
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  <Button
                    variant="contained"
                    onClick={() => handleAddSpecification(newSpecName, newSpecValue)}
                    fullWidth
                    sx={{
                      minHeight: 48,
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowSingleSpec(false);
                      setNewSpecName("");
                      setNewSpecValue("");
                    }}
                    fullWidth
                    sx={{
                      minHeight: 48,
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            )}

            {Object.keys(specifications).length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "#1f2937" }}
                >
                  Current Specifications
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(specifications).map(([key, value]) => (
                    <Grid item xs={12} key={key}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2fr 2fr auto",
                          gap: 12,
                          alignItems: "center",
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <TextField
                          value={key}
                          onChange={(e) =>
                            handleSpecificationChange(key, "name", e.target.value)
                          }
                          fullWidth
                          label="Name"
                          sx={{ backgroundColor: "white", borderRadius: 2 }}
                        />
                        <TextField
                          value={value}
                          onChange={(e) =>
                            handleSpecificationChange(key, "value", e.target.value)
                          }
                          fullWidth
                          label="Value"
                          sx={{ backgroundColor: "white", borderRadius: 2 }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteSpecification(key)}
                          sx={{
                            height: "100%",
                            textTransform: "none",
                            borderRadius: 2,
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Paper>

        {/* Pricing & Others Section */}
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ mb: { xs: 3, md: 4 } }}
            >
              <Inventory2Icon
                sx={{
                  fontSize: { xs: 20, sm: 24 },
                  color: "#2563eb",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: { xs: "1.125rem", sm: "1.25rem" },
                }}
              >
                Pricing & Others
              </Typography>
            </Stack>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Unit Price <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <Controller
                  name="unit_price"
                  control={control}
                  rules={{ required: "Unit price is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="0.00"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>₹</Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Min Order Qty
                </Typography>
                <Controller
                  name="min_order_qty"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="1"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Stock Quantity <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <Controller
                  name="stock_qty"
                  control={control}
                  rules={{ required: "Stock quantity is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="0"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Discount Type
                </Typography>
                <Controller
                  name="discount_type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      {...field}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    >
                      <MenuItem value="flat">Flat</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Discount Amount
                </Typography>
                <Controller
                  name="discount_amount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="0"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>
                            {watch("discount_type") === "percentage"
                              ? "%"
                              : "₹"}
                          </Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Tax Percentage
                </Typography>
                <Controller
                  name="tax_percent"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="0"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>%</Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Tax Calculation
                </Typography>
                <Controller
                  name="tax_calculation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      {...field}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    >
                      <MenuItem value="include">Include</MenuItem>
                      <MenuItem value="exclude">Exclude</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1, sm: 1.5 },
                    color: "#374151",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  }}
                >
                  Shipping Cost
                </Typography>
                <Controller
                  name="shipping_cost"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="0.00"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1 }}>₹</Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          py: { xs: 1.5, sm: 2 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="shipping_multiply_quantity"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label={
                        field.value
                          ? "Multiply with quantity"
                          : "Flat shipping cost"
                      }
                      sx={{ mt: 1 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
        {/* Product Variation Setup Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 4 }}
            >
              <AutorenewIcon sx={{ fontSize: 24, color: "#2563eb" }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                Product Variation Setup
              </Typography>
            </Stack>

            <Grid container spacing={3} alignItems="flex-start">
              {/* Dynamic Attributes Based on Category */}
              {getRelevantAttributes().map((attribute) => {
                const isEnabled = enabledAttributes.includes(attribute.name);
                const selectedValues = selectedAttributes[attribute.name] || [];

                return (
                  <Grid item xs={12} sm={6} md={4} key={attribute._id}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      {attribute.name}
                    </Typography>

                    {/* Toggle */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEnabled}
                          onChange={(e) =>
                            toggleAttribute(attribute.name, e.target.checked)
                          }
                        />
                      }
                      label={`Enable ${attribute.name}`}
                    />

                    {/* Values */}
                    {isEnabled && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {(attribute.values || []).map((val) => (
                            <Chip
                              key={val}
                              label={val}
                              clickable
                              onClick={() =>
                                toggleAttributeValue(attribute.name, val)
                              }
                              color={
                                selectedValues.includes(val)
                                  ? "primary"
                                  : "default"
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>

            {/* Generate Variations Button */}
            {(() => {
              console.log("🔍 VARIATIONS GENERATION DEBUG:");
              console.log("🔍 selectedAttributes:", selectedAttributes);
              console.log(
                "🔍 Object.keys(selectedAttributes):",
                Object.keys(selectedAttributes),
              );
              console.log("🔍 enabledAttributes:", enabledAttributes);
              console.log("🔍 attributes:", attributes);
              console.log(
                "🔍 getRelevantAttributes():",
                getRelevantAttributes(),
              );

              const shouldShow =
                Object.keys(selectedAttributes).length > 0 &&
                Object.keys(selectedAttributes).some(
                  (attrName) => selectedAttributes[attrName].length > 0,
                );

              console.log("🔍 Should show generate button:", shouldShow);
              return shouldShow;
            })() ? (
              <Box
                sx={{
                  mt: 4,
                  mb: 4,
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    console.log("Generate button clicked!");
                    console.log("selectedAttributes:", selectedAttributes);
                    console.log(
                      "selectedSizeValues:",
                      selectedAttributes["Size"] || [],
                    );
                    generateVariations();
                  }}
                  startIcon={<AutorenewIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#2563eb",
                    "&:hover": { backgroundColor: "#1d4ed8" },
                  }}
                >
                  Generate Variations
                </Button>
              </Box>
            ) : (
              <Box
                sx={{ mt: 4, mb: 4, p: 3, bgcolor: "#f8fafc", borderRadius: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", textAlign: "center" }}
                >
                  {Object.keys(selectedAttributes).length === 0
                    ? "Enable attributes (Color, Size, etc.) to generate variations"
                    : "Select at least one value (e.g., Red, Size M) to generate variations"}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Paper>

        {/* Generated Variations Table */}
        {(() => {
          console.log("🔍 Table render check:", {
            generatedVariations,
            length: generatedVariations.length,
            isArray: Array.isArray(generatedVariations),
          });
          return generatedVariations.length > 0;
        })() && (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              mt: 3,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: "#1e293b" }}
              >
                Generated Variations ({generatedVariations.length})
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e2e8f0",
                  overflowX: "auto",
                }}
              >
                <Table sx={{ minWidth: 700 }}>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        SL
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        Attribute Variation
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        Variation Wise Price (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        SKU
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        Variation Wise Stock
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generatedVariations.map((variation, index) => {
                      // Debug each variation - log FULL structure
                      console.log(`🔍 Variation ${index} - FULL OBJECT:`, JSON.stringify(variation, null, 2));
                      console.log(`🔍 Variation ${index} - ALL KEYS:`, Object.keys(variation));
                      console.log(`🔍 Variation ${index}:`, {
                        id: variation.id,
                        name: variation.name,
                        price: variation.price,
                        sku: variation.sku,
                        stock: variation.stock,
                        image: variation.image,
                        fullObject: variation,
                      });

                      // ✅ Support both field name structures for attributes
                      const attrText =
                        variation.name ||
                        Object.entries(variation.attributes || {})
                          .map(([key, value]) => `${key}: ${value}`)
                          .join(", ") ||
                        ["size", "color", "printType"]
                          .filter((key) => variation[key])
                          .map((key) => `${key}: ${variation[key]}`)
                          .join(", ");

                      return (
                        <TableRow key={variation.id || index} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {attrText}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <TextField
                              type="number"
                              value={variation.price || ""}
                              onChange={(e) =>
                                updateVariation(index, "price", e.target.value)
                              }
                              size="small"
                              sx={{ minWidth: 120 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    ₹
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <TextField
                              value={variation.sku || ""}
                              onChange={(e) =>
                                updateVariation(index, "sku", e.target.value)
                              }
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <TextField
                              type="number"
                              value={variation.stock || ""}
                              onChange={(e) =>
                                updateVariation(index, "stock", e.target.value)
                              }
                              size="small"
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Paper>
        )}

        {/* Product Image Management Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 4 }}
            >
              <CloudUploadIcon sx={{ fontSize: 24, color: "#2563eb" }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                Product Image Management
              </Typography>
            </Stack>

            <Grid container spacing={3} alignItems="flex-start">
              {/* Product Thumbnail Upload */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Product Thumbnail <span style={{ color: "#ef4444" }}>*</span>
                  <Tooltip title="Primary image for product listings and search results">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", mb: 2, display: "block" }}
                >
                  1:1 (500×500 px) - Max 2MB - JPEG/PNG/WebP
                </Typography>

                {productThumbnail ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: { xs: "100%", sm: 260, md: 300 },
                      height: { xs: 180, sm: 220, md: 240 },
                    }}
                  >
                    <Box
                      component="img"
                      src={productThumbnail.preview}
                      alt="Product thumbnail"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                      }}
                    />
                    <IconButton
                      onClick={removeProductThumbnail}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(239, 68, 68, 0.9)",
                        color: "white",
                        "&:hover": { backgroundColor: "#dc2626" },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        backgroundColor: "rgba(34, 197, 94, 0.9)",
                        color: "white",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ✓
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      width: { xs: "100%", sm: 260, md: 300 },
                      height: { xs: 180, sm: 220, md: 240 },
                      border: "2px dashed #cbd5e1",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      cursor: "pointer",
                      backgroundColor: "#f8fafc",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#2563eb",
                        backgroundColor: "#f1f5f9",
                      },
                    }}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 36, color: "#64748b", mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#475569",
                        textAlign: "center",
                      }}
                    >
                      Upload Thumbnail
                    </Typography>
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleProductThumbnailUpload}
                    />
                  </Button>
                )}
              </Grid>

              {/* Colour-Wise Product Image Upload */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Colour-Wise Images
                  <Tooltip title="Upload images for each color variant">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", mb: 2, display: "block" }}
                >
                  One image per color variant - 1:1 ratio
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                  {selectedAttributes["color"] &&
                  selectedAttributes["color"].length > 0 ? (
                    selectedAttributes["color"].map((color) => (
                      <Box key={color} sx={{ position: "relative" }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#374151", mb: 1, display: "block" }}
                        >
                          {color}
                        </Typography>
                        {colorWiseImages[color?.toLowerCase()] ? (
                          <Box
                            sx={{
                              position: "relative",
                              width: 120,
                              height: 120,
                            }}
                          >
                            <img
                              src={
                                colorWiseImages[color?.toLowerCase()].preview
                              }
                              alt={`${color} variant`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <IconButton
                              onClick={() =>
                                removeColorImage(color?.toLowerCase())
                              }
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                backgroundColor: "rgba(239, 68, 68, 0.9)",
                                color: "white",
                                "&:hover": { backgroundColor: "#dc2626" },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            {selectedColorImage === color && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  left: 4,
                                  backgroundColor: "rgba(34, 197, 94, 0.9)",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: 20,
                                  height: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                }}
                              >
                                ✓
                              </Box>
                            )}
                            <Chip
                              label={color}
                              size="small"
                              sx={{
                                position: "absolute",
                                bottom: 4,
                                right: 4,
                                backgroundColor: getColorHex(color),
                                color: "white",
                                fontSize: 10,
                                height: 20,
                              }}
                            />
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{
                              width: 120,
                              height: 120,
                              border: "2px dashed #cbd5e1",
                              borderRadius: 2,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#64748b",
                              backgroundColor: "#f8fafc",
                              "&:hover": {
                                backgroundColor: "#f1f5f9",
                                border: "2px dashed #94a3b8",
                              },
                            }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 24, mb: 0.5 }} />
                            <Typography variant="caption">Add</Typography>
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(e) => handleColorImageUpload(color, e)}
                            />
                          </Button>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        color: "#94a3b8",
                        backgroundColor: "#f8fafc",
                        borderRadius: 2,
                        border: "1px dashed #e2e8f0",
                      }}
                    >
                      <Typography variant="caption">
                        Select colors in Product Variation Setup to upload
                        color-wise images
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Additional Product Images */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Additional Gallery Images
                  <Tooltip title="Extra images for product details - angles, lifestyle, close-ups">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", mb: 2, display: "block" }}
                >
                  Multiple images allowed - 1:1 ratio recommended
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AddPhotoAlternateIcon />}
                    sx={{
                      borderRadius: 2,
                      border: "2px dashed #cbd5e1",
                      color: "#64748b",
                      backgroundColor: "#f8fafc",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                        border: "2px dashed #94a3b8",
                      },
                    }}
                  >
                    Upload Gallery Images
                    <input
                      hidden
                      accept="image/*"
                      multiple
                      type="file"
                      onChange={handleAdditionalImagesUpload}
                    />
                  </Button>
                </Box>

                {additionalImages.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {additionalImages.map((image, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <img
                          src={image.preview}
                          alt={`Gallery ${index + 1}`}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                            cursor: "pointer",
                          }}
                          onClick={() => selectAdditionalImage(index)}
                        />
                        <IconButton
                          onClick={() => removeAdditionalImage(index)}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            backgroundColor: "rgba(239, 68, 68, 0.9)",
                            color: "white",
                            "&:hover": { backgroundColor: "#dc2626" },
                            width: 20,
                            height: 20,
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        {selectedAdditionalImage === index && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 2,
                              left: 2,
                              backgroundColor: "rgba(34, 197, 94, 0.9)",
                              color: "white",
                              borderRadius: "50%",
                              width: 18,
                              height: 18,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                            }}
                          >
                            ✓
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
        {/* Product Customization & Mockup Setup Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 4 }}
            >
              <CloudUploadIcon sx={{ fontSize: 24, color: "#2563eb" }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                Product Customization & Mockup Setup
              </Typography>
            </Stack>
 
            <FormControlLabel
              control={
                <Switch
                  checked={customizationEnabled}
                  onChange={(e) => setCustomizationEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Product Customization"
              sx={{ mb: 3 }}
            />
 
            {customizationEnabled && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                    >
                      Customization Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={customizationType}
                      onChange={(e) => handleCustomizationTypeChange(e.target.value)}
                      sx={{
                        backgroundColor: "#f8fafc",
                        borderRadius: 2,
                      }}
                    >
                      {customizationTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                    >
                      Printable Areas
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {customizationPositionOptions.map((position) => (
                        <FormControlLabel
                          key={position}
                          control={
                            <Checkbox
                              checked={customizationPositions.includes(position)}
                              onChange={() => toggleCustomizationPosition(position)}
                            />
                          }
                          label={position}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
 
                {customizationPositions.length === 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      Select at least one printable area to upload mockups and define print zones.
                    </Typography>
                  </Box>
                )}
 
                {customizationPositions.map((position) => {
                  const mockup = customizationMockups[position];
                  const printArea = customizationPrintAreas[position] || {
                    x: 0.1,
                    y: 0.1,
                    width: 0.8,
                    height: 0.8,
                  };
                  return (
                    <Box
                      key={position}
                      sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 2, color: "#1f2937" }}
                      >
                        {position} Mockup
                      </Typography>
                      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {mockup ? (
                          <Box
                            sx={{
                              width: 160,
                              height: 160,
                              position: "relative",
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "1px solid #e2e8f0",
                              backgroundColor: "white",
                            }}
                          >
                            <Box
                              component="img"
                              src={mockup.preview}
                              alt={`${position} mockup`}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <IconButton
                              onClick={() =>
                                setCustomizationMockups((prev) => {
                                  const copy = { ...prev };
                                  delete copy[position];
                                  return copy;
                                })
                              }
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                backgroundColor: "rgba(239, 68, 68, 0.9)",
                                color: "white",
                                "&:hover": { backgroundColor: "#dc2626" },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            sx={{
                              width: 160,
                              height: 160,
                              border: "2px dashed #cbd5e1",
                              borderRadius: 2,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              color: "#64748b",
                              backgroundColor: "#f8fafc",
                              "&:hover": {
                                backgroundColor: "#f1f5f9",
                                borderColor: "#94a3b8",
                              },
                            }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="caption">Upload {position} Mockup</Typography>
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(e) => handleCustomizationMockupUpload(position, e)}
                            />
                          </Button>
                        )}
 
                        <Box sx={{ flex: 1, minWidth: 220 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                          >
                            Printable Area Coordinates
                          </Typography>
                          <Grid container spacing={2}>
                            {[
                              ["x", "X"],
                              ["y", "Y"],
                              ["width", "Width"],
                              ["height", "Height"],
                            ].map(([field, label]) => (
                              <Grid item xs={6} sm={3} key={`${position}-${field}`}>
                                <TextField
                                  label={label}
                                  type="number"
                                  inputProps={{ step: 0.01, min: 0, max: 1 }}
                                  value={printArea[field] ?? ""}
                                  onChange={(e) => updateCustomizationPrintArea(position, field, e.target.value)}
                                  fullWidth
                                  sx={{ backgroundColor: "white", borderRadius: 2 }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1 }}>
                            Coordinates are percentages relative to the mockup image (0.0 - 1.0).
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
 
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 2, color: "#1f2937" }}
                  >
                    Available Customization Tools
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(customizationToolOptions).map(([tool, enabled]) => (
                      <Grid item xs={12} sm={6} md={4} key={tool}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enabled}
                              onChange={() => handleCustomizationToolToggle(tool)}
                            />
                          }
                          label={
                            tool === "textColor"
                              ? "Text Color"
                              : tool === "font"
                              ? "Font Selection"
                              : tool.charAt(0).toUpperCase() + tool.slice(1)
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}
          </CardContent>
        </Paper>
        {/* Product Video Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 4 }}
            >
              <Inventory2Icon sx={{ fontSize: 24, color: "#2563eb" }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                Product Video
              </Typography>
            </Stack>

            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  YouTube Video Link
                  <Tooltip title="Use embed link format: https://www.youtube.com/embed/VIDEO_ID">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Controller
                  name="video_link"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      placeholder="https://www.youtube.com/embed/VIDEO_ID"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        "Example: https://www.youtube.com/embed/5R06LRdUCSE"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                            borderColor: "#cbd5e1",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor: "#2563eb",
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
        {/* SEO Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 4 }}
            >
              <Inventory2Icon sx={{ fontSize: 24, color: "#2563eb" }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#1e293b" }}
              >
                SEO Section
              </Typography>
            </Stack>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                onClick={handleAutoFillMeta}
                sx={{
                  borderColor: "#2563eb",
                  color: "#2563eb",
                  "&:hover": {
                    backgroundColor: "rgba(37, 99, 235, 0.04)",
                    borderColor: "#1d4ed8",
                  },
                }}
              >
                Auto-Fill Meta Fields
              </Button>
            </Box>

            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Meta Title
                  <Tooltip title="Best length is 50-60 characters for SEO">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Controller
                  name="meta_title"
                  control={control}
                  rules={{ maxLength: 60 }}
                  render={({ field, fieldState }) => (
                    <Box>
                      <TextField
                        {...field}
                        placeholder="Meta Title"
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ maxLength: 60 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              borderColor: "#cbd5e1",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              borderColor: "#2563eb",
                              boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", mt: 0.5, display: "block" }}
                      >
                        {field.value?.length || 0}/60 characters
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Meta Image (2:1)
                  <Tooltip title="Image shown in social shares and Google snippets">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", mb: 2, display: "block" }}
                >
                  Recommended: 1200×600 px
                </Typography>

                {metaImage ? (
                  <Box
                    sx={{ position: "relative", width: "100%", height: 120 }}
                  >
                    <img
                      src={metaImage.preview}
                      alt="Meta image preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}
                    />
                    <IconButton
                      onClick={removeMetaImage}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(239, 68, 68, 0.9)",
                        color: "white",
                        "&:hover": { backgroundColor: "#dc2626" },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        backgroundColor: "rgba(34, 197, 94, 0.9)",
                        color: "white",
                        borderRadius: 2,
                        px: 1,
                        py: 0.5,
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      2:1
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      width: "100%",
                      height: 120,
                      border: "2px dashed #cbd5e1",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748b",
                      backgroundColor: "#f8fafc",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                        border: "2px dashed #94a3b8",
                      },
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="body2">Upload Meta Image</Typography>
                    <Typography variant="caption">2:1 ratio</Typography>
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleMetaImageUpload}
                    />
                  </Button>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
                >
                  Meta Description
                  <Tooltip title="Used by search engines; helps ranking">
                    <InfoIcon sx={{ fontSize: 16, ml: 1, color: "#94a3b8" }} />
                  </Tooltip>
                </Typography>
                <Controller
                  name="meta_description"
                  control={control}
                  rules={{ maxLength: 160 }}
                  render={({ field, fieldState }) => (
                    <Box>
                      <TextField
                        {...field}
                        placeholder="Meta Description"
                        fullWidth
                        multiline
                        rows={3}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ maxLength: 160 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              borderColor: "#cbd5e1",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              borderColor: "#2563eb",
                              boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", mt: 0.5, display: "block" }}
                      >
                        {field.value?.length || 0}/160 characters
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              SEO Indexing & Crawling Controls
            </Typography>

            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 2, color: "#374151" }}
                >
                  Indexing Options
                </Typography>

                <Controller
                  name="indexing_option"
                  control={control}
                  defaultValue="index"
                  render={({ field }) => (
                    <Box>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={field.value === "index"}
                            onChange={() => field.onChange("index")}
                          />
                        }
                        label="Index"
                        sx={{ mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={field.value === "noindex"}
                            onChange={() => field.onChange("noindex")}
                          />
                        }
                        label="No Index"
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ ml: 3 }}>
                        <FormControlLabel
                          control={<Checkbox size="small" />}
                          label="No Follow"
                          sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                          control={<Checkbox size="small" />}
                          label="No Archive"
                          sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                          control={<Checkbox size="small" />}
                          label="No Image Index"
                          sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                          control={<Checkbox size="small" />}
                          label="No Snippet"
                        />
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 2, color: "#374151" }}
                >
                  Snippet Control Settings
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Controller
                    name="max_snippet"
                    control={control}
                    defaultValue="-1"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max Snippet"
                        type="number"
                        helperText="-1 = unlimited, 0 = no snippet"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="max_video_preview"
                    control={control}
                    defaultValue="-1"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max Video Preview (seconds)"
                        type="number"
                        helperText="-1 = unlimited"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="max_image_preview"
                    control={control}
                    defaultValue="large"
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Max Image Preview</InputLabel>
                        <Select {...field} label="Max Image Preview">
                          <MenuItem value="none">Small</MenuItem>
                          <MenuItem value="standard">Medium</MenuItem>
                          <MenuItem value="large">Large</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>

        {/* Form Action Buttons */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: "white",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => reset()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#cbd5e1",
                    color: "#64748b",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                      borderColor: "#94a3b8",
                    },
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                      boxShadow: "0 6px 20px rgba(37, 99, 235, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {loading
                    ? "Saving..."
                    : isEditMode
                      ? "Update Product"
                      : "Save Product"}
                </Button>
              </Stack>
            </Grid>
          </CardContent>
        </Paper>
      </form>
    </Box>
  );
}
